/**
 * WebSidebar — navigation latérale pour l'expérience web desktop.
 *
 * Affichée uniquement sur web ≥ 768px (sinon TabBar bottom mobile).
 * Largeur fixe 240px, fond clair, logo MyTenue cursive en haut, items verticaux
 * avec état actif. Utilise un navigationRef pour piloter le NavigationContainer.
 *
 * v1.12.117 — refonte web app desktop.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, Easing, Platform, Image } from 'react-native';
import { TABS, TabIcon } from '../navigation/tabConfig';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { colors } from '../theme';

const SIDEBAR_WIDTH = 240;

function MyTenueWordmark({ color }) {
  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const lineAnim = React.useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 700, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.timing(lineAnim, { toValue: 1, duration: 600, delay: 250, easing: Easing.out(Easing.quad), useNativeDriver: false }),
    ]).start();
  }, []);
  return (
    <View style={{ alignItems: 'flex-start' }}>
      <Animated.Text style={{
        fontFamily: Platform.select({
          ios: 'Snell Roundhand',
          android: 'serif',
          web: "'Snell Roundhand', 'Brush Script MT', 'Lucida Handwriting', cursive",
          default: 'serif',
        }),
        fontStyle: 'italic',
        fontWeight: '700',
        fontSize: 32,
        color,
        lineHeight: 38,
        opacity: fadeAnim,
        textShadowColor: 'rgba(26, 24, 20, 0.18)',
        textShadowOffset: { width: 0, height: 2 },
        textShadowRadius: 5,
      }}>mytenue</Animated.Text>
      <Animated.View style={{
        marginTop: 4,
        height: 2,
        backgroundColor: '#9B7B5A',
        borderRadius: 1,
        opacity: 0.9,
        width: lineAnim.interpolate({ inputRange: [0, 1], outputRange: [0, 64] }),
      }} />
    </View>
  );
}

export default function WebSidebar({ navRef }) {
  const { dark, theme } = useTheme();
  const { user, signOut } = useAuth();
  const { t, lang } = useI18n();
  const [currentRoute, setCurrentRoute] = useState('Home');
  // v1.12.148 — popup au clic sur le footer (Profil + Se déconnecter)
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Suivre la route active via le navigationRef
  useEffect(() => {
    if (!navRef?.current) return undefined;
    const refresh = () => {
      try {
        const r = navRef.current?.getCurrentRoute?.();
        if (r?.name) setCurrentRoute(r.name);
      } catch {}
    };
    refresh(); // initial
    let unsub;
    try {
      unsub = navRef.current.addListener?.('state', refresh);
    } catch {}
    return () => {
      try { unsub?.(); } catch {}
    };
  }, [navRef]);

  const onNavigate = (key) => {
    // v1.12.220+221 — Quitter un onglet à filtres → reset à l'état par défaut
    const FILTERED_TABS = ['Catalogue', 'Garde-robe', 'Suggestions', 'Voyage'];
    if (FILTERED_TABS.includes(currentRoute) && key !== currentRoute) {
      try {
        const { DeviceEventEmitter } = require('react-native');
        DeviceEventEmitter.emit('tabs_reset_to_default', { from: currentRoute, to: key });
      } catch {}
    }
    try {
      // v1.12.118 — depuis Login/SignUp/ConfirmEmail/ForgotPassword (modals
      // au-dessus de Main), cliquer la sidebar doit fermer ces modals et
      // basculer sur la tab demandée. navigate('Main', { screen: tabKey })
      // pop la stack jusqu'à Main puis switch le tab.
      navRef.current?.navigate?.('Main', { screen: key });
    } catch (e) {
      // Fallback : ancien comportement (juste switch tab si on est déjà sur Main)
      try { navRef.current?.navigate?.(key); } catch {}
      console.warn('[WebSidebar] navigate failed:', e?.message);
    }
  };

  const colorOn  = theme.tabActive   || colors.text.primary;
  const colorOff = theme.tabInactive || '#8B8478';
  const itemBgActive = dark ? 'rgba(196, 168, 130, 0.10)' : 'rgba(155, 123, 90, 0.08)';

  // v1.12.155 — distingue mode connecté vs invité
  const isAuthed = !!user;
  // Nom utilisateur (court) pour le footer
  const displayName = (user?.firstName || user?.name || user?.email || '').toString().split(/[\s@]/)[0] || 'Vous';
  const tier = user?.tier || 'free';
  const tierLabel = tier === 'gold' ? 'Gold' : tier === 'premium' ? 'Premium' : tier === 'basic' ? 'Basic' : 'Free';

  return (
    <View style={[s.sidebar, { backgroundColor: theme.bgSurface || theme.bg, borderRightColor: theme.bgBorder }]}>
      {/* Logo header */}
      <View style={s.header}>
        {/* v1.12.842 — Logo Gold trimmed, taille reduite */}
        <Image
          source={require('../../assets/logo-sidebar.png')}
          style={{ width: 130, height: 130, resizeMode: 'contain' }}
        />
        <Text style={[s.tagline, { color: theme.textFaint }]}>{lang === 'en' ? 'YOUR AI STYLIST' : 'VOTRE STYLISTE IA'}</Text>
      </View>

      {/* Nav items */}
      <View style={s.navList}>
        {TABS.map(tab => {
          const focused = currentRoute === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              activeOpacity={0.7}
              onPress={() => onNavigate(tab.key)}
              style={[s.navItem, focused && { backgroundColor: itemBgActive }]}
            >
              <View style={[s.navIcon, focused && s.navIconActive]}>
                <TabIcon name={tab.icon} active={focused} colorOn={colorOn} colorOff={colorOff} size={22} />
              </View>
              <Text
                numberOfLines={1}
                style={[
                  s.navLabel,
                  { color: focused ? colorOn : colorOff },
                  focused && s.navLabelActive,
                ]}
              >
                {tab.labelKey ? t(tab.labelKey) : tab.label}
              </Text>
              {focused && <View style={[s.activeBar, { backgroundColor: '#9B7B5A' }]} />}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Footer : v1.12.155 — invité = "Se connecter" / connecté = popup Profil/Logout */}
      {!isAuthed ? (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => {
            try { navRef.current?.navigate?.('Login'); }
            catch (e) { console.warn('[WebSidebar] nav Login failed:', e?.message); }
          }}
          style={[s.footer, { borderTopColor: theme.bgBorder }]}
        >
          <View style={[s.avatar, { backgroundColor: '#1A1A1A' }]}>
            <Text style={[s.avatarInitial, { color: '#D4AF72' }]}>↪</Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text numberOfLines={1} style={[s.userName, { color: theme.text }]}>{t('sidebar_signin')}</Text>
            <Text style={[s.userTier, { color: theme.textMuted }]}>{t('sidebar_guest_account')}</Text>
          </View>
          <Text style={{ fontSize: 14, color: theme.textMuted, marginLeft: 4 }}>›</Text>
        </TouchableOpacity>
      ) : (
        <View style={{ position: 'relative' }}>
          {showUserMenu && (
            <>
              {/* Overlay invisible pour fermer au clic en dehors */}
              <TouchableOpacity
                activeOpacity={1}
                onPress={() => setShowUserMenu(false)}
                style={{
                  position: 'absolute',
                  left: -SIDEBAR_WIDTH * 4, top: -2000,
                  width: '500vw', height: '500vh',
                  zIndex: 1000,
                }}
              />
              {/* Menu popup */}
              <View style={[s.userMenu, { backgroundColor: theme.bgCard, borderColor: theme.bgBorder }]}>
                <TouchableOpacity
                  style={s.userMenuItem}
                  activeOpacity={0.6}
                  onPress={() => { setShowUserMenu(false); onNavigate('Profil'); }}
                >
                  <Text style={s.userMenuIcon}>👤</Text>
                  <Text style={[s.userMenuText, { color: theme.text }]}>{t('sidebar_my_profile')}</Text>
                </TouchableOpacity>
                <View style={[s.userMenuDivider, { backgroundColor: theme.bgBorder }]} />
                <TouchableOpacity
                  style={s.userMenuItem}
                  activeOpacity={0.6}
                  onPress={async () => {
                    setShowUserMenu(false);
                    try { await signOut?.(); } catch (e) { console.warn('[WebSidebar] signOut failed:', e?.message); }
                  }}
                >
                  <Text style={s.userMenuIcon}>↩</Text>
                  <Text style={[s.userMenuText, { color: '#C62828' }]}>{t('sidebar_sign_out')}</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={() => setShowUserMenu(v => !v)}
            style={[s.footer, { borderTopColor: theme.bgBorder, backgroundColor: showUserMenu ? (dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : 'transparent' }]}
          >
            <View style={s.avatar}>
              <Text style={s.avatarInitial}>{(displayName[0] || '?').toUpperCase()}</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text numberOfLines={1} style={[s.userName, { color: theme.text }]}>{displayName}</Text>
              <Text style={[s.userTier, { color: '#9B7B5A' }]}>{tierLabel}</Text>
            </View>
            <Text style={{ fontSize: 12, color: theme.textMuted, marginLeft: 4 }}>
              {showUserMenu ? '▾' : '▸'}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* v1.12.153 — Footer légal (TimeOne / réseaux affiliation) */}
      <View style={{ paddingHorizontal: 16, paddingTop: 8, paddingBottom: 4 }}>
        <Text style={{ fontSize: 9, color: theme.textFaint, textAlign: 'center', lineHeight: 12 }}>
          {t('sidebar_edited_by')}{'\n'}
          {t('sidebar_address')}
        </Text>
        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 8, marginTop: 4 }}>
          <TouchableOpacity
            onPress={() => { try { navRef.current?.navigate?.('Terms'); } catch (e) { console.warn('[WebSidebar] nav Terms failed:', e?.message); } }}
            activeOpacity={0.6}>
            <Text style={{ fontSize: 9, color: theme.textMuted, textDecorationLine: 'underline' }}>{t('sidebar_terms')}</Text>
          </TouchableOpacity>
          <Text style={{ fontSize: 9, color: theme.textFaint }}>·</Text>
          <TouchableOpacity
            onPress={() => { try { navRef.current?.navigate?.('Privacy'); } catch (e) { console.warn('[WebSidebar] nav Privacy failed:', e?.message); } }}
            activeOpacity={0.6}>
            <Text style={{ fontSize: 9, color: theme.textMuted, textDecorationLine: 'underline' }}>{t('sidebar_privacy')}</Text>
          </TouchableOpacity>
          <Text style={{ fontSize: 9, color: theme.textFaint }}>·</Text>
          <TouchableOpacity
            onPress={() => { try { navRef.current?.navigate?.('ConditionsVente'); } catch (e) { console.warn('[WebSidebar] nav CGV failed:', e?.message); } }}
            activeOpacity={0.6}>
            <Text style={{ fontSize: 9, color: theme.textMuted, textDecorationLine: 'underline' }}>{t('sidebar_cgv')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

export { SIDEBAR_WIDTH };

const s = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_WIDTH,
    height: '100%',
    borderRightWidth: 1,
    paddingTop: 28,
    paddingBottom: 16,
    flexDirection: 'column',
  },
  header: {
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  tagline: {
    fontSize: 9,
    letterSpacing: 2.5,
    marginTop: 12,
    fontWeight: '500',
  },
  navList: {
    flex: 1,
    paddingHorizontal: 12,
    gap: 2,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 11,
    paddingHorizontal: 12,
    borderRadius: 10,
    position: 'relative',
    gap: 12,
  },
  navIcon: {
    width: 26,
    alignItems: 'center',
  },
  navIconActive: {},
  navLabel: {
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 0.2,
    flex: 1,
  },
  navLabelActive: {
    fontWeight: '700',
  },
  activeBar: {
    position: 'absolute',
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 2,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 14,
    borderTopWidth: 1,
    marginTop: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#9B7B5A',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  userName: {
    fontSize: 13,
    fontWeight: '600',
  },
  userTier: {
    fontSize: 10,
    letterSpacing: 0.8,
    marginTop: 2,
    fontWeight: '600',
  },
  // v1.12.148 — Popup menu user
  userMenu: {
    position: 'absolute',
    left: 12, right: 12, bottom: 76,
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
    zIndex: 1001,
  },
  userMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 10,
  },
  userMenuIcon: {
    fontSize: 14,
  },
  userMenuText: {
    fontSize: 13,
    fontWeight: '500',
  },
  userMenuDivider: {
    height: 1,
    marginHorizontal: 8,
  },
});
